import { FormattedMessage, useIntl } from "react-intl";
import { logout } from "@/auth/actions";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { useAuthStore } from "@/stores/auth-store";

// Shown when a live session ends underneath the user (ticket #67).
//
// One way out, and it leaves the page (ticket #177). The dialog used to offer a
// second, dismissing exit that merely hid it, which put the user back in a fully
// rendered but unauthenticated UI - the exact state #67 set out to end.
//
// The single action reads as "sign in again" because that is the user's intent;
// the logout underneath it is plumbing. It matters anyway: /auth/me only strips
// the auth key from the expired session, so without a logout the cookie lingers
// for the session TTL. POST /auth/logout deletes it outright - and revokes at
// Keycloak too whenever a refresh token is still held, which on the plain
// expiry path it is not: that token's absence is what raised this notice in the
// first place. The current path rides along, so the user comes back where they
// were.
//
// The cost is that unsaved edits go with it, but by the time we know the session
// is gone the autosave that would have rescued them can no longer reach the
// server either. The body copy says so before the button is pressed.
//
// Neutral by design. Nothing has gone wrong beyond the unsaved edits already
// named in the body, so this carries no alarm styling.

export function SessionExpiredNotice() {
  const intl = useIntl();
  const expired = useAuthStore((s) => s.sessionExpired);
  // "tokens-lost" means no time limit was reached (see auth-store), and this
  // dialog only gets to that state after a silent recovery was declined. Saying
  // "reached its time limit" there would be a plain untruth, so the body drops
  // the claim rather than inventing a cause.
  const reason = useAuthStore((s) => s.sessionEndedReason);
  // Esc and backdrop click land here too - no dismissal path may leave the
  // user behind in the stale UI.
  const signInAgain = (): void => void logout(window.location.pathname + window.location.search);

  return (
    <Modal
      open={expired}
      onClose={signInAgain}
      title={intl.formatMessage({
        id: "auth.sessionExpired.title",
        defaultMessage: "Session ended",
      })}
      footer={
        <div className="flex justify-end">
          <Button variant="primary" onClick={signInAgain}>
            <FormattedMessage id="auth.sessionExpired.signIn" defaultMessage="Sign in again" />
          </Button>
        </div>
      }
    >
      <p className="text-[13px] leading-relaxed text-ink/70">
        {reason === "tokens-lost" ? (
          <FormattedMessage
            id="auth.sessionExpired.bodyTokensLost"
            defaultMessage="You have been signed out. Changes are no longer being saved. Sign in again to continue where you left off."
          />
        ) : (
          <FormattedMessage
            id="auth.sessionExpired.body"
            defaultMessage="You have been signed out because the session reached its time limit. Changes are no longer being saved. Sign in again to continue where you left off."
          />
        )}
      </p>
    </Modal>
  );
}
